import { useState, useContext } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Box,
  Divider,
  Tooltip,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import CategoryIcon from '@mui/icons-material/Category';
import HandshakeIcon from '@mui/icons-material/Handshake';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import GroupIcon from '@mui/icons-material/Group';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BadgeIcon from '@mui/icons-material/Badge';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LoginIcon from '@mui/icons-material/Login';
import { Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import ThemeModeContext from '../contexts/ThemeModeContext';
import { useI18n } from '../contexts/I18nContext';

export default function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user, logout } = useContext(AuthContext);
  const { mode, toggleMode } = useContext(ThemeModeContext);
  const { lang, setLang, t } = useI18n();

  const menuItems = [
    { key: 'register', text: t('menu.register'), path: '/register', roles: ['ADMIN', 'EMPLOYEE'], icon: <PointOfSaleIcon fontSize="small" />, group: 'ops' },
    { key: 'products', text: t('menu.products'), path: '/products', roles: ['ADMIN'], icon: <CategoryIcon fontSize="small" />, group: 'ops' },
    { key: 'stock', text: t('menu.stock'), path: '/stock', roles: ['ADMIN'], icon: <Inventory2Icon fontSize="small" />, group: 'ops' },
    { key: 'partners', text: t('menu.partners'), path: '/partners', roles: ['ADMIN'], icon: <HandshakeIcon fontSize="small" />, group: 'ops' },
    { key: 'suppliers', text: t('menu.suppliers'), path: '/suppliers', roles: ['ADMIN'], icon: <LocalShippingIcon fontSize="small" />, group: 'ops' },
    { key: 'cash', text: t('menu.cash'), path: '/cash', roles: ['ADMIN'], icon: <AccountBalanceWalletIcon fontSize="small" />, group: 'fin' },
    { key: 'finance', text: t('menu.finance'), path: '/finance', roles: ['ADMIN'], icon: <AnalyticsIcon fontSize="small" />, group: 'fin' },
    { key: 'expenses', text: t('menu.expenses'), path: '/expenses', roles: ['ADMIN'], icon: <ReceiptLongIcon fontSize="small" />, group: 'fin' },
    { key: 'users', text: t('menu.users'), path: '/users', roles: ['ADMIN'], icon: <GroupIcon fontSize="small" />, group: 'admin' },
    { key: 'incoming_documents', text: 'Dokumente', path: '/incoming-documents', roles: ['ADMIN'], icon: <CloudUploadIcon fontSize="small" />, group: 'admin' },
    { key: 'payroll', text: t('menu.payroll'), path: '/payroll', roles: ['ADMIN', 'EMPLOYEE'], icon: <BadgeIcon fontSize="small" />, group: 'admin' },
    { key: 'dashboard', text: t('menu.dashboard'), path: '/dashboard', roles: ['ADMIN'], icon: <DashboardIcon fontSize="small" />, group: 'admin' },
  ].filter((item) => user && item.roles.includes(user.role));

  const groups = [
    { key: 'ops', title: 'Operations' },
    { key: 'fin', title: 'Finance' },
    { key: 'admin', title: 'Administration' },
  ];

  return (
    <>
      <AppBar
        position="static"
        dir="ltr"
        elevation={0}
        sx={{
          background: 'var(--navbar-bg)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <Toolbar>
          <Box
            component={Link}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
              textDecoration: 'none',
              gap: { xs: 1, sm: 1.5 },
            }}
          >
            <Box
              component="img"
              src="/logo.svg"
              alt="MOUSAIDI"
              sx={{
                height: { xs: 44, sm: 54 },
                width: { xs: 166, sm: 214 },
                objectFit: 'contain',
                objectPosition: 'left center',
                mr: { xs: 0.5, sm: 1.5 },
                ml: { xs: -0.5, sm: 0 },
                filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.28))',
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setLang(lang === 'fr' ? 'ar' : 'fr')}
              sx={{ minWidth: 88, borderRadius: 999 }}
            >
              {lang === 'fr' ? 'العربية' : 'FR'}
            </Button>
            <Tooltip title={mode === 'dark' ? t('theme.dark') : t('theme.light')}>
              <IconButton
                aria-label="Theme umschalten"
                onClick={toggleMode}
                sx={{
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--icon-button-bg)',
                  border: '1px solid var(--icon-button-border)',
                  '&:hover': {
                    backgroundColor: 'var(--icon-button-hover-bg)',
                  },
                }}
              >
                {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            {user ? (
              <IconButton
                edge="end"
                onClick={() => setDrawerOpen(true)}
                sx={{
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--icon-button-bg)',
                  border: '1px solid var(--icon-button-border)',
                  '&:hover': {
                    backgroundColor: 'var(--icon-button-hover-bg)',
                  },
                }}
              >
                <MenuIcon fontSize="medium" />
              </IconButton>
            ) : (
              <Tooltip title={t('login.submit')}>
                <IconButton
                  component={Link}
                  to="/login"
                  aria-label={t('login.submit')}
                  sx={{
                    color: '#ffffff',
                    background: 'linear-gradient(90deg, #16a34a, #059669)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    '&:hover': {
                      background: 'linear-gradient(90deg, #15803d, #047857)',
                    },
                  }}
                >
                  <LoginIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer anchor="right" open={Boolean(user && drawerOpen)} onClose={() => setDrawerOpen(false)}>
        <Box
          sx={{
            width: 260,
            height: '100%',
            background: 'var(--drawer-bg)',
            color: 'var(--text-primary)',
          }}
          role="presentation"
        >
          <List sx={{ py: 1 }}>
            {groups.map((group) => {
              const groupItems = menuItems.filter((item) => item.group === group.key);
              if (groupItems.length === 0) return null;
              return (
                <Box key={group.key}>
                  <ListSubheader
                    sx={{
                      bgcolor: 'transparent',
                      color: 'var(--text-muted)',
                      fontWeight: 800,
                      lineHeight: '28px',
                    }}
                  >
                    {group.title}
                  </ListSubheader>
                  {groupItems.map((item) => (
                    <ListItem key={item.key} disablePadding>
                      <ListItemButton
                        component={Link}
                        to={item.path}
                        onClick={() => setDrawerOpen(false)}
                        sx={{
                          px: 3,
                          py: 1.2,
                          borderRadius: 2,
                          mx: 1,
                          '&:hover': { backgroundColor: 'var(--row-hover-bg)' },
                        }}
                      >
                        <ListItemIcon sx={{ color: 'var(--text-secondary)', minWidth: 34 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{ fontWeight: 700, letterSpacing: 0.2 }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </Box>
              );
            })}

            <Divider sx={{ my: 1, opacity: 0.2 }} />

            <ListItem disablePadding>
              <ListItemButton
                onClick={(e) => {
                  e.stopPropagation();
                  setDrawerOpen(false);
                  logout();
                }}
                sx={{
                  px: 3,
                  py: 1.4,
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                  '&:hover': { backgroundColor: 'rgba(239,68,68,0.18)' },
                }}
              >
                <ListItemText
                  primary={t('menu.logout')}
                  primaryTypographyProps={{ fontWeight: 700, letterSpacing: 0.3 }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
}
